import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase, apiBaseUrl } from '../../lib/supabase';
import { Database } from '../../types/supabase';

type Organization = Database['public']['Tables']['organizations']['Row'];
type OrganizationInsert = Database['public']['Tables']['organizations']['Insert'];

interface OrganizationState {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  organization: null,
  loading: false,
  error: null,
};

export const fetchOrganizationProfile = createAsyncThunk(
  'organization/fetchProfile',
  async (organizationId: string) => {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single();

    if (error) throw error;
    return data;
  }
);

// Goes through the server (admin/manager only) rather than a direct RLS
// write - the old upsert() took the whole client-held org object, including
// columns like subscription_tier a plain member should never be able to
// touch. The route allowlists which fields are actually editable.
export const saveOrganizationProfile = createAsyncThunk(
  'organization/saveProfile',
  async (organization: OrganizationInsert) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not signed in');
    // The route's zod schema allowlists editable fields and silently drops
    // everything else, so sending the whole in-memory org object here is
    // safe - id/subscription_tier/etc. never reach the update.
    const res = await fetch(`${apiBaseUrl}/api/organization`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(organization),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(body?.error ?? `Request failed (${res.status})`);
    return body.organization as Organization;
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    updateProfile: (state, action: PayloadAction<Partial<Organization>>) => {
      if (state.organization) {
        state.organization = { ...state.organization, ...action.payload };
      }
    },
    updateThreatProfile: (state, action: PayloadAction<Organization['threat_profile']>) => {
      if (state.organization) {
        state.organization.threat_profile = action.payload;
      }
    },
    updateComplianceRequirements: (state, action: PayloadAction<Organization['compliance_requirements']>) => {
      if (state.organization) {
        state.organization.compliance_requirements = action.payload;
      }
    },
    addSecurityFramework: (state, action: PayloadAction<string>) => {
      if (state.organization) {
        state.organization.security_frameworks.push(action.payload);
      }
    },
    removeSecurityFramework: (state, action: PayloadAction<string>) => {
      if (state.organization) {
        state.organization.security_frameworks = state.organization.security_frameworks.filter(
          (framework) => framework !== action.payload
        );
      }
    },
    addDataType: (state, action: PayloadAction<string>) => {
      if (state.organization) {
        state.organization.data_types.push(action.payload);
      }
    },
    removeDataType: (state, action: PayloadAction<string>) => {
      if (state.organization) {
        state.organization.data_types = state.organization.data_types.filter(
          (type) => type !== action.payload
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganizationProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(fetchOrganizationProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch organization profile';
      })
      .addCase(saveOrganizationProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveOrganizationProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(saveOrganizationProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save organization profile';
      });
  },
});

export const {
  updateProfile,
  updateThreatProfile,
  updateComplianceRequirements,
  addSecurityFramework,
  removeSecurityFramework,
  addDataType,
  removeDataType,
} = organizationSlice.actions;

export default organizationSlice.reducer;
